import React from "react";

type DynamicJSONCardProps = {
    header: string;
    content: any;
}

const DynamicJSONCard = ({
    header,
    content,
}: DynamicJSONCardProps) => {

    // Helper to render array values as individual tags
    const renderArrayAsTags = (array: any[]) => (
        <span className="flex flex-wrap gap-1 inline-flex">
            {array.map((item, i) => (
                <span key={i} className="font-normal bg-light-green px-2 py-1 rounded">
                    {item}
                </span>
            ))}
        </span>
    );

    // Helper to render boolean values as checkboxes
    const renderBoolean = (value: boolean) => (
        <input
            type="checkbox"
            checked={value}
            readOnly
            className="h-4 w-4 accent-secondary-gray cursor-default self-end mb-1"
        />
    );

    // Function to render content based on its type
    const renderContent = (data: any) => {
        if (typeof data === 'object' && data !== null) {
            return (
                <div className="w-full">
                    {Object.entries(data).map(([key, value], index, arr) => (
                        <div
                            key={index}
                            className={`py-3 ${index !== arr.length - 1 ? 'border-b border-gray-lines' : ''}`}
                        >
                            {Array.isArray(value) ? (
                                <div className="flex items-baseline">
                                    <span className="font-medium mr-2">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                    {renderArrayAsTags(value)}
                                </div>
                            ) : typeof value === 'object' && value !== null ? (
                                <div>
                                    <div className="font-medium">{key.charAt(0).toUpperCase() + key.slice(1)}</div>
                                    <div className="ml-4 mt-2 w-full">
                                        {Object.entries(value as object).map(([subKey, subValue], subIndex, subArr) => (
                                            <div
                                                key={subIndex}
                                                className={`pb-2 ${subIndex !== subArr.length - 1 ? 'mb-2 border-b border-gray-lines' : ''}`}
                                            >
                                                {typeof subValue === 'boolean' ? (
                                                    <div className="flex items-end">
                                                        {renderBoolean(subValue)}
                                                        <span className="font-medium ml-2">{subKey.charAt(0).toUpperCase() + subKey.slice(1)}</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="font-medium mr-2">{subKey.charAt(0).toUpperCase() + subKey.slice(1)}</span>
                                                        {Array.isArray(subValue)
                                                            ? renderArrayAsTags(subValue)
                                                            : <span className="font-semibold text-dark-green">
                                                                {String(subValue)}
                                                            </span>
                                                        }
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start w-full">
                                    {typeof value === 'boolean' ? (
                                        <>
                                            {renderBoolean(value)}
                                            <span className="font-medium ml-2">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="font-medium mr-2 self-start">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                            <span className="font-semibold text-off-black self-start">
                                                {String(value)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        } else if (Array.isArray(data)) {
            return (
                <div className="w-full">
                    {renderArrayAsTags(data)}
                </div>
            );
        } else {
            return typeof data === 'boolean'
                ? renderBoolean(data)
                : <p className="font-semibold text-dark-green w-full">{String(data)}</p>;
        }
    };

    return (
        <div className="w-full">
            <div className="flex flex-col items-start w-full mt-4 mb-3 bg-light-gray border border-gray-lines rounded px-4 py-3 relative">
                <div className="w-full">
                    <label className="text-lg font-semibold text-secondary-gray">{header}</label>
                </div>
                <div className="flex flex-col justify-start items-start w-full mt-2">
                    {renderContent(content)}
                </div>
            </div>
        </div>
    );
};

export default DynamicJSONCard;